import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button, Card, Checkbox, Col, Form, Input, InputNumber, Popconfirm, Row, Segmented,
  Space, Switch, Table, Tag, TimePicker, Tooltip, Typography, message,
} from "antd";
import { DeleteOutlined, EditOutlined, PlusOutlined, SaveOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import Alert from "../../components/GxAlert";
import GxNote from "../../components/GxNote";
import MonthCalendarCard from "./MonthCalendarCard";
import {
  createHoliday, deleteDayRule, deleteHoliday, getDayRules, getHolidays,
  getAccountingSettings, getWorkSchedule, updateAccountingSettings, updateWorkDay,
  upsertDayRule,
} from "../../api/client";
import { apiError } from "../../utils/apiError";
import { JalaliDateInput, JALALI_MONTHS, formatDurationFa, toJalali } from "../../utils/dateUtils";
import { faDigits, faNum } from "../../utils/faNumberFormat";

const { Text } = Typography;
const WEEK = ["شنبه", "یکشنبه", "دوشنبه", "سه‌شنبه", "چهارشنبه", "پنجشنبه", "جمعه"];

/**
 * تنظیماتِ حضور و غیاب — **کنارِ خودِ کار**، نه در تنظیماتِ کلیِ سیستم.
 *
 * چرا جابه‌جا شد: کسی که دارد حضورِ ماه را می‌بندد، همان‌جا لازم دارد تعطیلی و
 * ساعتِ کاری و تلورانس را ببیند؛ رفتن به «تنظیمات ← ...» یعنی گم‌کردنِ رشتهٔ کار.
 */
export default function AttendanceSettingsTab({ year, month, onChanged }) {
  const [s, setS] = useState(null);
  const [sched, setSched] = useState([]);
  const [hol, setHol] = useState([]);
  const [rules, setRules] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [a, b, c, d] = await Promise.all([
        getAccountingSettings(), getWorkSchedule(), getHolidays({ year }),
        getDayRules(year, month),
      ]);
      setS(a.data); setSched(b.data); setHol(c.data); setRules(d.data);
    } catch (e) { message.error(apiError(e, "خطا در بارگذاری تنظیمات")); }
  }, [year, month]);
  useEffect(() => { load(); }, [load]);

  const saveSettings = async (patch) => {
    setSaving(true);
    try {
      await updateAccountingSettings({ ...s, ...patch });
      // 🐞 پاسخِ PATCH فقط {message} است؛ قبلاً همان جای تنظیمات می‌نشست و فرم خالی می‌شد
      setS({ ...s, ...patch }); message.success("ذخیره شد");
    } catch (e) { message.error(apiError(e, "ذخیره نشد")); }
    setSaving(false);
  };

  const saveDay = async (weekday, patch) => {
    try {
      await updateWorkDay(weekday, patch);
      const b = await getWorkSchedule(); setSched(b.data);
    } catch (e) { message.error(apiError(e, "ذخیره نشد")); }
  };

  // ── تعطیلیِ تازه ──
  const [hDate, setHDate] = useState(null);
  const [hKind, setHKind] = useState("paid_off");
  const [hDesc, setHDesc] = useState("");
  const addHoliday = async () => {
    if (!hDate) return message.warning("تاریخ را انتخاب کنید");
    try {
      await createHoliday({ holiday_date: hDate, kind: hKind, description: hDesc || null });
      setHDate(null); setHDesc("");
      const c = await getHolidays({ year }); setHol(c.data);
      message.success("تعطیلی ثبت شد");
    } catch (e) { message.error(apiError(e, "ثبت نشد")); }
  };

  // ── اصلاحِ همگانیِ یک روز ──
  const [rDate, setRDate] = useState(null);
  /* ساعتِ **عادیِ** روزِ انتخاب‌شده. بدونِ نشان‌دادنش، کاربر همان ۱۸:۰۰ِ عادی
     را دوباره وارد می‌کند، ثبت می‌شود، هیچ اثری ندارد، و بعد به‌درستی
     می‌گوید «اصلاح زدم ولی لحاظ نشد». */
  const normalWindow = useMemo(() => {
    if (!rDate || !sched?.length) return "";
    const jsDow = new Date(`${String(rDate).slice(0, 10)}T00:00:00`).getDay();
    const wd = (jsDow + 1) % 7;                       // شنبه = ۰
    const row = sched.find((x) => x.weekday === wd);
    if (!row) return "";
    if (!row.is_working) return "روزِ غیرکاری";
    return `${String(row.start_time).slice(0, 5)}–${String(row.end_time).slice(0, 5)}`;
  }, [rDate, sched]);
  /* v2.152.6 (کاربر ۱۴۰۵/۰۸/۰۲): «فقط پایانِ آن روز را دارد؛ شاید بخواهیم ورود را اصلاح کنیم یا خروج را — هر کدام را
     زدیم همان اصلاح شود». سرور و محاسبه از قبل `start_time` را می‌فهمیدند؛ فقط فرم نداشت. خالی = همان ساعتِ عادی. */
  const [rStart, setRStart] = useState(null);
  const [rEnd, setREnd] = useState(null);
  const [rGrace, setRGrace] = useState(0);
  const [rNote, setRNote] = useState("");
  /* ثبت کلِ ردیفِ آن روز را جایگزین می‌کند ⇒ اگر برای آن روز اصلاحی هست، فرم با همان پر می‌شود تا با زدنِ فقط «شروع»
     پایانِ قبلی بی‌صدا پاک نشود. */
  const pickRuleDate = (d) => {
    setRDate(d);
    const ex = (rules || []).find((x) => x.work_date === String(d || "").slice(0, 10));
    setRStart(ex?.start_time ? dayjs(ex.start_time, "HH:mm") : null);
    setREnd(ex?.end_time ? dayjs(ex.end_time, "HH:mm") : null);
    setRGrace(ex?.grace_minutes || 0);
    setRNote(ex?.note || "");
  };
  const addRule = async () => {
    if (!rDate) return message.warning("تاریخ را انتخاب کنید");
    if (!rStart && !rEnd && !rGrace) return message.warning("ساعتِ شروع، ساعتِ پایان یا دقیقهٔ ارفاق را بگذارید");
    if (rStart && rEnd && rEnd.isBefore(rStart)) return message.warning("پایان باید بعد از شروع باشد");
    try {
      const r = await upsertDayRule({
        work_date: rDate,
        start_time: rStart ? rStart.format("HH:mm") : null,
        end_time: rEnd ? rEnd.format("HH:mm") : null,
        grace_minutes: rGrace || 0,
        note: rNote || null,
      });
      setRDate(null); setRStart(null); setREnd(null); setRGrace(0); setRNote("");
      const d = await getDayRules(year, month); setRules(d.data);
      /* سرور می‌گوید این اصلاح واقعاً چیزی عوض کرد یا نه. «ثبت شد»ِ خشک،
         همان چیزی بود که باعث شد کاربر ماهِ بعد بگوید «زدم ولی لحاظ نشد». */
      if (r?.data?.warning) message.warning(r.data.message, 8);
      else message.success(r?.data?.message || "اصلاحِ همگانی ثبت شد");
      onChanged?.();
    } catch (e) { message.error(apiError(e, "ثبت نشد")); }
  };

  const monthHol = hol.filter((h) => {
    const j = h.jalali_date || "";
    return j.startsWith(`${year}/${String(month).padStart(2, "0")}`);
  });

  return (
    <Space direction="vertical" size={12} style={{ width: "100%" }}>
      <GxNote id="att-settings" signature="v1"
        message={`تنظیماتِ حضور و غیاب — ${JALALI_MONTHS[month - 1]} ${faDigits(year)}`}>
        هرچه اینجا تغییر کند مستقیم روی محاسبهٔ کسرکار و غیبتِ همین ماه اثر
        می‌گذارد. تعطیلیِ «باحقوق» روزِ کاری نیست و غیبت هم برایش ثبت نمی‌شود؛
        تعطیلیِ «کارگاه باز» مثلِ روزِ عادی حساب می‌شود.
      </GxNote>

      {/* ── ۱) تقویمِ ماه — اول از همه، چون بقیه رویش سوارند ── */}
      <MonthCalendarCard year={year} month={month} holidays={hol}
        schedule={sched} dayRules={rules} onChanged={load} />

      {/* ── ۲) ساعتِ کاریِ هفته ── */}
      <Card size="small" title="۲) ساعتِ کاریِ هفته">
        <Table size="small" pagination={false} rowKey="weekday" dataSource={sched}
          scroll={{ x: "max-content" }}
          columns={[
            { title: "روز", dataIndex: "weekday", width: 90,
              render: (v) => WEEK[v] || v },
            { title: "روزِ کاری", dataIndex: "is_working", width: 90,
              render: (v, r) => <Switch checked={v}
                onChange={(c) => saveDay(r.weekday, { is_working: c })} /> },
            { title: "باحقوق", dataIndex: "is_paid", width: 90,
              render: (v, r) => <Switch checked={v}
                onChange={(c) => saveDay(r.weekday, { is_paid: c })} /> },
            { title: "شروع", dataIndex: "start_time", width: 110,
              render: (v, r) => <TimePicker format="HH:mm" allowClear={false}
                disabled={!r.is_working} value={v ? dayjs(v, "HH:mm:ss") : null}
                onChange={(t) => saveDay(r.weekday, { start_time: t.format("HH:mm:ss") })} /> },
            { title: "پایان", dataIndex: "end_time", width: 110,
              render: (v, r) => <TimePicker format="HH:mm" allowClear={false}
                disabled={!r.is_working} value={v ? dayjs(v, "HH:mm:ss") : null}
                onChange={(t) => saveDay(r.weekday, { end_time: t.format("HH:mm:ss") })} /> },
            { title: "موظفی", dataIndex: "expected_minutes", width: 100,
              render: (v) => <Tag color="blue">{formatDurationFa(v)}</Tag> },
          ]} />
      </Card>

      {/* ── ۳) تعطیلی‌های ماه ── */}
      <Card size="small" title="۳) فهرستِ متنیِ تعطیلی‌ها (پشتیبان)">
        <Space wrap style={{ marginBottom: 10 }}>
          <JalaliDateInput value={hDate} onChange={setHDate} />
          <Segmented value={hKind} onChange={setHKind}
            options={[{ value: "paid_off", label: "تعطیلِ باحقوق" },
              { value: "working", label: "کارگاه باز" }]} />
          <Input placeholder="مناسبت (اختیاری)" value={hDesc} style={{ width: 200 }}
            onChange={(e) => setHDesc(e.target.value)} />
          <Button type="primary" icon={<PlusOutlined />} onClick={addHoliday}>افزودن</Button>
        </Space>
        <Table size="small" pagination={false} rowKey="id" dataSource={monthHol}
          locale={{ emptyText: "برای این ماه تعطیلیِ ثبت‌شده‌ای نیست (جمعه‌ها پیش‌فرض تعطیل‌اند)" }}
          columns={[
            { title: "تاریخ", dataIndex: "jalali_date", width: 120,
              render: (v) => faDigits(v) },
            { title: "نوع", dataIndex: "kind", width: 130,
              render: (v) => (v === "paid_off"
                ? <Tag color="green">تعطیلِ باحقوق</Tag> : <Tag color="orange">کارگاه باز</Tag>) },
            { title: "مناسبت", dataIndex: "description", render: (v) => v || "—" },
            { title: "", width: 60,
              render: (_, r) => (
                <Popconfirm title="حذف شود؟" okText="بله" cancelText="خیر"
                  onConfirm={async () => {
                    await deleteHoliday(r.id);
                    const c = await getHolidays({ year }); setHol(c.data);
                  }}>
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              ) },
          ]} />
      </Card>

      {/* ── ۴) اصلاحِ همگانیِ یک روز ── */}
      <Card size="small" title="۴) اصلاحِ همگانیِ ساعتِ یک روز">
        <Alert type="info" showIcon style={{ marginBottom: 10 }}
          message="روزی که دستگاه خراب شد یا کارگاه زودتر تعطیل کرد"
          description={"به‌جای بخشیدنِ نفربه‌نفر، همین‌جا یک‌بار برای همه تعیین کنید: "
            + "ساعتِ شروع (ورود) و/یا پایان (خروج)ِ آن روز را عوض کنید، یا چند دقیقه ارفاق بدهید؛ "
            + "هر کدام خالی بماند همان ساعتِ عادی است. "
            + "ترددِ ثبت‌شده دست نمی‌خورد — فقط کسری دوباره حساب می‌شود، پس قابلِ برگشت است."} />
        <Space wrap style={{ marginBottom: 10 }}>
          <JalaliDateInput value={rDate} onChange={pickRuleDate} />
          <span>شروعِ (ورودِ) آن روز:</span>
          <TimePicker format="HH:mm" value={rStart} onChange={setRStart} placeholder="--:--" />
          <span>پایانِ (خروجِ) آن روز:</span>
          <TimePicker format="HH:mm" value={rEnd} onChange={setREnd} placeholder="--:--" />
          {normalWindow && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              (ساعتِ عادیِ این روز: {faDigits(normalWindow)})
            </Text>
          )}
          <span>یا ارفاق (دقیقه):</span>
          <InputNumber min={0} max={600} value={rGrace} onChange={setRGrace} style={{ width: 90 }} />
          <Input placeholder="علت (مثلاً خرابیِ دستگاه)" value={rNote} style={{ width: 220 }}
            onChange={(e) => setRNote(e.target.value)} />
          <Button type="primary" icon={<PlusOutlined />} onClick={addRule}>ثبت</Button>
        </Space>
        <Table size="small" pagination={false} rowKey="id" dataSource={rules}
          locale={{ emptyText: "برای این ماه اصلاحِ همگانی ثبت نشده" }}
          columns={[
            { title: "تاریخ", dataIndex: "jalali_date", width: 120,
              render: (v) => faDigits(v) },
            { title: "شروعِ جدید", dataIndex: "start_time", width: 100,
              render: (v) => (v ? faDigits(v) : "—") },
            { title: "پایانِ جدید", dataIndex: "end_time", width: 100,
              render: (v) => (v ? faDigits(v) : "—") },
            { title: "ارفاق", dataIndex: "grace_minutes", width: 90,
              render: (v) => (v ? formatDurationFa(v) : "—") },
            { title: "علت", dataIndex: "note", render: (v) => v || "—" },
            /* بدونِ این ستون، ردیفی که هیچ کاری نمی‌کند عیناً مثلِ ردیفِ
               مؤثر دیده می‌شود — و همان‌جاست که اعتماد به عدد از بین می‌رود. */
            { title: "اثرِ واقعی", dataIndex: "effect", width: 230,
              render: (e) => {
                if (!e) return "—";
                if (e.is_noop) return (
                  <Tooltip title={`ساعتِ عادیِ این روز ${faDigits(e.normal_start)}–${faDigits(e.normal_end)} است؛ همان را دوباره وارد کرده‌اید.`}>
                    <Tag color="red">بدونِ اثر — همان ساعتِ عادی</Tag>
                  </Tooltip>
                );
                return (
                  <Space size={4} wrap>
                    <Tag color="green">{faDigits(e.label)}</Tag>
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      تا {formatDurationFa(e.minutes)} از کسری کم می‌شود
                    </Text>
                  </Space>
                );
              } },
            { title: "", width: 90,
              render: (_, r) => (
                <Space size={4}>
                  <Tooltip title="ویرایش (فرمِ بالا با همین مقادیر پر می‌شود)">
                    <Button size="small" icon={<EditOutlined />} onClick={() => pickRuleDate(r.work_date)} />
                  </Tooltip>
                  <Popconfirm title="حذف شود؟ کسریِ آن روز به حالت عادی برمی‌گردد."
                    okText="بله" cancelText="خیر"
                    onConfirm={async () => {
                      await deleteDayRule(r.id);
                      const d = await getDayRules(year, month); setRules(d.data);
                    }}>
                    <Button size="small" danger icon={<DeleteOutlined />} />
                  </Popconfirm>
                </Space>
              ) },
          ]} />
      </Card>
      {/* ── ۵) قوانینِ محاسبه ── */}
      <Card size="small" title="۵) قوانینِ محاسبه">
        {s && (
          <Row gutter={[12, 12]}>
            <Col xs={24} md={8}>
              <Text>آستانهٔ تلورانسِ کسری (دقیقه)</Text>
              <InputNumber style={{ width: "100%" }} min={0} max={240}
                value={s.shortage_tolerance_minutes}
                onChange={(v) => setS({ ...s, shortage_tolerance_minutes: v })} />
              <Text type="secondary" style={{ fontSize: 11 }}>
                کمتر از این مقدار در روز، کسرکار حساب نمی‌شود.
              </Text>
            </Col>
            <Col xs={24} md={8}>
              <Text>دامنهٔ تلورانس</Text>
              <Segmented block value={s.shortage_tolerance_scope}
                onChange={(v) => setS({ ...s, shortage_tolerance_scope: v })}
                options={[{ value: "daily", label: "روزانه" },
                  { value: "monthly", label: "ماهانه" }]} />
            </Col>
            <Col xs={24} md={8}>
              <Text>مبنای روزِ حقوقی (ساعت)</Text>
              <InputNumber style={{ width: "100%" }} min={1} max={24}
                value={s.overtime_base_hours_per_day}
                onChange={(v) => setS({ ...s, overtime_base_hours_per_day: v })} />
              <Text type="secondary" style={{ fontSize: 11 }}>
                هر {faNum(Number(s.overtime_base_hours_per_day || 7))} ساعت کسرکار =
                یک روزِ حقوقی ({formatDurationFa(Number(s.overtime_base_hours_per_day || 7) * 60)}).
              </Text>
            </Col>
            <Col xs={24} md={12}>
              <Space>
                <Switch checked={!!s.holiday_adjacent_absence_penalty}
                  onChange={(v) => setS({ ...s, holiday_adjacent_absence_penalty: v })} />
                <Text>جریمهٔ غیبتِ مجاورِ تعطیلی</Text>
              </Space>
              <div>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  غیبتِ چسبیده به تعطیلی، آن تعطیلی را هم غیبت می‌کند.
                </Text>
              </div>
            </Col>
            <Col xs={24} md={12}>
              <Space>
                <Switch checked={!!s.thursday_counts_as_full_day}
                  onChange={(v) => setS({ ...s, thursday_counts_as_full_day: v })} />
                <Text>پنجشنبهٔ کامل = یک روزِ کاملِ کاری</Text>
              </Space>
            </Col>
            <Col xs={24} md={12}>
              <Space>
                <Switch checked={s.count_early_exit !== false}
                  onChange={(v) => setS({ ...s, count_early_exit: v })} />
                <Text>تعجیل (زودرفتن) کسر شود</Text>
              </Space>
              <div>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  خاموش = فقط دیرکرد کسر می‌شود؛ زود رفتن نه در کسرکار می‌آید نه در ستونِ تعجیل.
                </Text>
              </div>
            </Col>
            {/* اضافه‌کاریِ خودکار (v2.108.0) — پیش‌فرض خاموش: فقط ثبتِ دستیِ ماه پول می‌دهد */}
            <Col span={24}>
              <div style={{ border: "1px dashed var(--gx-border, #ddd)", borderRadius: 8, padding: 10 }}>
                <Space>
                  <Switch checked={!!s.overtime_pay_enabled}
                    onChange={(v) => setS({ ...s, overtime_pay_enabled: v })} />
                  <Text strong>اضافه‌کاریِ خودکار از دستگاه حساب و پرداخت شود</Text>
                </Space>
                <div>
                  <Text type="secondary" style={{ fontSize: 11 }}>
                    خاموش = هیچ اضافه‌کاری‌ای از دستگاه نه نمایش داده می‌شود نه پرداخت؛ موردِ خاص را در
                    «ثبتِ دستیِ ماه» یا ردیفِ اصلاحیِ حقوق وارد کنید. روشن = فقط بعد از پایانِ ساعتِ
                    کاری، فقط در روزهای انتخاب‌شده و فقط تا سقفِ ساعت (نرخ = روزانه ÷ مبنای ساعت).
                  </Text>
                </div>
                {s.overtime_pay_enabled && (
                  <Space wrap style={{ marginTop: 8 }} align="start">
                    <div>
                      <Text style={{ fontSize: 12 }}>روزهای مجاز</Text>
                      <div>
                        <Checkbox.Group
                          value={(s.overtime_weekdays || "").split(",").filter((x) => x !== "").map(Number)}
                          onChange={(arr) => setS({ ...s,
                            overtime_weekdays: arr.length ? [...arr].sort().join(",") : null })}
                          options={WEEK.map((w, i) => ({ value: i, label: w }))} />
                      </div>
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        هیچ‌کدام = همهٔ روزهای کاری (بدونِ جمعه/تعطیل). روزِ تعطیلِ انتخاب‌شده: کلِ کارکرد اضافه‌کاری است.
                      </Text>
                    </div>
                    <div>
                      <Text style={{ fontSize: 12 }}>سقفِ ساعت</Text>
                      <div>
                        <TimePicker format="HH:mm" minuteStep={5} allowClear placeholder="بی‌سقف"
                          value={s.overtime_until ? dayjs(s.overtime_until, "HH:mm") : null}
                          onChange={(t) => setS({ ...s, overtime_until: t ? t.format("HH:mm") : null })} />
                      </div>
                      <Text type="secondary" style={{ fontSize: 11 }}>مثلاً ۲۱:۰۰ — کارِ بعد از آن شمرده نمی‌شود.</Text>
                    </div>
                  </Space>
                )}
              </div>
            </Col>
            <Col span={24}>
              <Button type="primary" icon={<SaveOutlined />} loading={saving}
                onClick={() => saveSettings({})}>ذخیرهٔ قوانین</Button>
            </Col>
          </Row>
        )}
      </Card>

    </Space>
  );
}
